import { message } from 'antd';
import { printSelectedRecords } from '../actions/records';
import { CAPABILITIES, selectionLacksCapability } from './inboxRowCapabilities';

// Matches Api::V1::RecordsController::PRINT_MAX_RECORDS.
export const PRINT_SELECTED_LIMIT = 25;
export const PRINT_LIMIT_MESSAGE = `Select up to ${PRINT_SELECTED_LIMIT} documents to print.`;

/**
 * The one gate every Print selected button uses. Print sends the same files as Download, so
 * it shares Download's capability, adds the batch cap, and refuses revoked rows up front
 * instead of letting the server refuse the whole batch.
 * @param {Object[]} rows - The resolved selection (the whole page when select-all is used).
 */
export const printSelectionState = (rows) => {
  const overLimit = rows.length > PRINT_SELECTED_LIMIT;
  const denied = !rows.length
    || overLimit
    || selectionLacksCapability(rows, CAPABILITIES.DOWNLOAD)
    || rows.some((row) => row.record_status === 'revoked');
  return { overLimit, denied, tooltip: overLimit ? PRINT_LIMIT_MESSAGE : null };
};

const MESSAGE_KEY = 'print-selected';
// The tab keeps the PDF loaded; the blob URL only needs to live long enough to open it.
const BLOB_URL_LIFETIME_MS = 10 * 60 * 1000;
const FALLBACK_ERROR = 'Unable to prepare the selected records for printing. Please try again.';
const POPUP_BLOCKED_ERROR = 'Your browser blocked the print tab. Allow pop-ups for Medsender and try again.';

const documentsLabel = (count) => `${count} document${count === 1 ? '' : 's'}`;

// The tab has to open on the click (or popup blockers swallow it), seconds before the PDF exists.
// Fill it with a loading screen so it never sits blank. The markup is static: no record data.
const LOADING_STYLE = [
  'body{margin:0;height:100vh;display:flex;flex-direction:column;align-items:center;justify-content:center;',
  'font-family:-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,sans-serif;color:#2b2b3a;background:#fafafc}',
  '.spinner{width:40px;height:40px;border:4px solid #e4e0ee;border-top-color:#6b2fa0;border-radius:50%;',
  'animation:spin .8s linear infinite;margin-bottom:20px}',
  '@keyframes spin{to{transform:rotate(360deg)}}',
  'h1{font-size:18px;font-weight:600;margin:0 0 6px}p{font-size:14px;color:#6b6b7b;margin:0}',
].join('');

const showLoadingScreen = (printWindow, label) => {
  const { document } = printWindow;
  document.title = `Preparing ${label}…`;
  const style = document.createElement('style');
  style.textContent = LOADING_STYLE;
  document.head.appendChild(style);
  document.body.innerHTML = '<div class="spinner"></div>'
    + `<h1>Preparing ${label} for printing…</h1>`
    + '<p>The combined PDF will open here when it is ready.</p>';
};

// The endpoint answers errors as JSON, but the request asked for a blob.
const errorMessage = async (error) => {
  try {
    const body = JSON.parse(await error.response.data.text());
    return body.error || FALLBACK_ERROR;
  } catch {
    return FALLBACK_ERROR;
  }
};

/**
 * Combines the selected rows into one PDF and opens it in a new tab, so the whole batch
 * prints through one print dialog. Callers gate the button with printSelectionState; the
 * server still refuses the whole batch if any row cannot be printed.
 * @param {Object[]} rows - Selected inbox rows, in list order.
 */
export const printSelected = async (rows) => {
  if (printSelectionState(rows).denied) return;

  // Open during the click so popup blockers do not swallow the PDF, as single print does.
  // A blocked tab has nowhere to show the PDF, so skip building it.
  const printWindow = window.open();
  if (!printWindow) {
    message.error({ content: POPUP_BLOCKED_ERROR, key: MESSAGE_KEY });
    return;
  }
  try {
    showLoadingScreen(printWindow, documentsLabel(rows.length));
    message.loading({ content: `Preparing ${documentsLabel(rows.length)}…`, key: MESSAGE_KEY, duration: 0 });
    const response = await printSelectedRecords(rows.map((row) => row.file_token));

    const pdfUrl = URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
    printWindow.location.href = pdfUrl;
    printWindow.opener = null;
    setTimeout(() => URL.revokeObjectURL(pdfUrl), BLOB_URL_LIFETIME_MS);
    message.success({ content: `${documentsLabel(rows.length)} ready to print.`, key: MESSAGE_KEY });
  } catch (error) {
    if (!printWindow.closed) printWindow.close();
    message.error({ content: error.response ? await errorMessage(error) : FALLBACK_ERROR, key: MESSAGE_KEY });
  }
};
